/**
 * InvestorPmsPreview — Reservas & Rendimiento (Checkpoint C).
 *
 * Vista futura demo del investor sobre la operativa de SU villa. Se muestra
 * dentro del tab `pms_preview` del InvestorDashboard actual. Todos los
 * números derivan del dataset PMS único (pmsDemoData) vía selectors puros.
 *
 * Villa demo: V01 (Villa Ombak, Dolce Vistas). Cuando el proyecto real esté
 * operativo cada investor verá su propia villa.
 *
 * Privacidad: nunca se muestran datos de otras villas ni PII de huésped.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Section } from "@/pages/Shell";
import {
  Info, X, Sparkle, CaretDown, ArrowRight, DownloadSimple, Bed,
  ChartBar, House, Calendar, Coin, Scales,
} from "@phosphor-icons/react";
import {
  DEMO_MONTH, DEMO_MONTH_LABEL, DEMO_TODAY_ISO, POOLS,
  BOOKING_STATUS_LABEL, STAY_STATUS_LABEL,
} from "@/data/pmsDemoData";
import {
  getVilla, getPool, getChannel,
  villaMonthStats, villaFinancialBreakdown, villaChannelMix,
  villaUpcomingReservations, villaForwardBookings, villaYtdCombined,
  villaYtdOwnerNet,
  villaMonthlyEvolution, ownerStayUsage, ownerStayValidation,
  statementsForVilla, reservationsForVilla, blocksForVilla,
  monthRange, datesBetweenExclusive, addDays, isNightInStay, isDayInBlock,
  formatEUR, formatPct, effectiveBlockEnd,
} from "@/lib/pmsSelectors";
import {
  usePmsBlocks, usePmsOwnerStays, createOwnerStayRequest,
} from "@/lib/pmsRuntimeStore";

/** Villa concreta usada para la demo del investor. */
const DEMO_INVESTOR_VILLA_ID = "V01";
/** Identidad demo del investor logueado (V01). */
const DEMO_INVESTOR = { user_id: "inv_v01", name: "Elena Ruiz" };

const CAL_LEGEND = [
  { key: "reserved",  label: "Reservada",       cls: "bg-[#9C5236]" },
  { key: "owner",     label: "Uso propietario", cls: "bg-stone-800 dark:bg-stone-300" },
  { key: "blocked",   label: "Bloqueada",       cls: "bg-red-400 dark:bg-red-500" },
  { key: "available", label: "Disponible",      cls: "bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700" },
];

export default function InvestorPmsPreview() {
  usePmsBlocks(); // reactividad si aparece un maintenance block runtime
  usePmsOwnerStays(); // reactividad al crear/approve/reject de owner stays

  const villa = getVilla(DEMO_INVESTOR_VILLA_ID);
  const pool = getPool(villa.pool_id);
  const monthStats = useMemo(() => villaMonthStats(villa.villa_id, DEMO_MONTH), [villa]);
  const breakdown = useMemo(() => villaFinancialBreakdown(villa.villa_id, DEMO_MONTH), [villa]);
  const channelMix = useMemo(() => villaChannelMix(villa.villa_id, DEMO_MONTH), [villa]);
  const upcoming = useMemo(() => villaUpcomingReservations(villa.villa_id, DEMO_TODAY_ISO, 5), [villa]);
  const forward = useMemo(() => villaForwardBookings(villa.villa_id, DEMO_TODAY_ISO, 90), [villa]);
  const ytd = useMemo(() => villaYtdCombined(villa.villa_id, DEMO_MONTH), [villa]);
  const ytdOwnerNet = useMemo(() => villaYtdOwnerNet(villa.villa_id), [villa]);
  const evolution = useMemo(() => villaMonthlyEvolution(villa.villa_id), [villa]);
  const ownerUsage = ownerStayUsage(villa.villa_id, 2028);
  const statements = statementsForVilla(villa.villa_id)
    .slice()
    .sort((a, b) => (a.month < b.month ? 1 : -1));

  const [showBreakdown, setShowBreakdown] = useState(false);
  const [statementModal, setStatementModal] = useState(null);
  const [smartModalOpen, setSmartModalOpen] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [evoMetric, setEvoMetric] = useState("revenue"); // revenue | occupancy | owner_net

  const directShare = channelMix
    .filter((r) => r.channel.kind === "direct")
    .reduce((acc, r) => acc + r.share, 0);
  const otaCommissionAvoided = (() => {
    // Estimación DEMO: si las noches directas hubieran ido por Booking (18%),
    // el revenue perdido en comisión sería `directRevenue * 0.18`.
    const directRev = channelMix
      .filter((r) => r.channel.kind === "direct")
      .reduce((acc, r) => acc + r.revenue, 0);
    return directRev * 0.18;
  })();

  const upcomingOwnerStay = ownerUsage.requests.find(
    (r) => r.status === "approved" && r.end_date >= DEMO_TODAY_ISO,
  );

  return (
    <Section
      title="Reservas & Rendimiento"
      subtitle={`Vista futura de la operativa de tu villa · ${DEMO_MONTH_LABEL}`}
    >
      <VillaHeader villa={villa} pool={pool} />

      {/* KPIs principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="pms-inv-kpis">
        <Kpi label="Ocupación"        value={formatPct(monthStats.occupancy)} sub={`${monthStats.guest_nights} / ${monthStats.commercial_nights} noches`} tone="emerald" />
        <Kpi label="ADR"              value={monthStats.guest_nights ? formatEUR(monthStats.revenue_month / monthStats.guest_nights) : "—"} sub="por noche" hint="Tarifa media por noche ocupada." />
        <Kpi label="Ingresos"         value={formatEUR(monthStats.revenue_month)} sub="accommodation" />
        <Kpi label="Neto estimado"    value={formatEUR(breakdown.owner_net_estimated)} sub="tras costes" tone="terracotta"
             hint="Estimación después de tasas, distribución, gastos operacionales y management. El importe final se confirma al cierre mensual." />
      </div>

      <MonthStatusBar statements={statements} />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* LEFT: calendar + upcoming + evolution */}
        <div className="lg:col-span-2 space-y-4">
          <VillaCalendar villa={villa} />

          <BlockCard title="Próximas reservas" icon={Calendar} testId="pms-inv-upcoming">
            {upcoming.length === 0 ? (
              <div className="text-sm text-stone-500 dark:text-stone-400">Sin reservas próximas.</div>
            ) : (
              <ul className="divide-y divide-stone-100 dark:divide-stone-900">
                {upcoming.map((r) => (
                  <UpcomingRow key={r.reservation_id} r={r} />
                ))}
              </ul>
            )}
          </BlockCard>

          <BlockCard title="Evolución" icon={ChartBar} testId="pms-inv-evolution"
            headerRight={
              <div className="inline-flex bg-stone-100 dark:bg-stone-800 rounded-full p-0.5">
                {[
                  { key: "revenue", label: "Ingresos" },
                  { key: "occupancy", label: "Ocupación" },
                  { key: "owner_net", label: "Neto" },
                ].map((opt) => {
                  const active = evoMetric === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setEvoMetric(opt.key)}
                      className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                        active
                          ? "bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 shadow-sm"
                          : "text-stone-500 dark:text-stone-400"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            }
          >
            <EvolutionChart series={evolution} metric={evoMetric} />
          </BlockCard>
        </div>

        {/* RIGHT: YTD + channel mix + owner + smart + forward + statements */}
        <div className="space-y-4">
          <BlockCard title="YTD 2028" icon={Coin} testId="pms-inv-ytd">
            <YtdBlock ytd={ytd} ownerNet={ytdOwnerNet} />
          </BlockCard>

          <BlockCard title="Mix de canales" icon={ChartBar} testId="pms-inv-channels">
            {channelMix.length === 0 ? (
              <div className="text-sm text-stone-500 dark:text-stone-400">Sin reservas este mes.</div>
            ) : (
              <>
                <div className="space-y-2">
                  {channelMix.map((row) => (
                    <ChannelRow key={row.channel.key} row={row} />
                  ))}
                </div>
                <div className="mt-3 text-xs text-stone-700 dark:text-stone-300">
                  <strong>{Math.round(directShare * 100)}%</strong> de tus reservas llegaron
                  directamente a través de Dolce.
                </div>
                {otaCommissionAvoided > 0 && (
                  <div className="mt-1 text-[11px] text-stone-500 dark:text-stone-400">
                    Comisiones OTA evitadas (estimación demo): {formatEUR(otaCommissionAvoided)}.
                  </div>
                )}
              </>
            )}
          </BlockCard>

          <BlockCard title="Uso de mi villa" icon={Bed} testId="pms-inv-owner-usage">
            <OwnerUsageBlock
              usage={ownerUsage}
              upcoming={upcomingOwnerStay}
              onRequest={() => setOwnerModalOpen(true)}
            />
          </BlockCard>

          <BlockCard title="Distribución inteligente" icon={Scales} testId="pms-inv-smart">
            <p className="text-sm text-stone-700 dark:text-stone-300">
              Tu villa pertenece al pool <strong>{pool?.label}</strong>, formado por
              {" "}{pool?.villa_count} unidades equivalentes. Las oportunidades de reserva se
              distribuyen mediante un sistema que considera ingresos, ocupación y disponibilidad
              comercial para favorecer un reparto equilibrado.
            </p>
            <button
              type="button"
              onClick={() => setSmartModalOpen(true)}
              className="mt-3 text-xs text-stone-900 dark:text-stone-100 hover:underline inline-flex items-center gap-1"
              data-testid="pms-inv-smart-how"
            >
              Cómo funciona <ArrowRight size={12} />
            </button>
            <p className="mt-3 text-[11px] text-stone-500 dark:text-stone-400">
              El sistema busca equilibrar oportunidades comerciales. No garantiza ingresos u
              ocupación idénticos entre villas.
            </p>
          </BlockCard>

          <BlockCard title="Próximos 90 días" icon={Calendar} testId="pms-inv-forward">
            <div className="text-sm text-stone-700 dark:text-stone-300">
              <div className="flex justify-between mb-1">
                <span>Actualmente reservado</span>
                <span className="tabular-nums font-medium">{forward.nights} noches</span>
              </div>
              <div className="flex justify-between">
                <span>Ingresos ya reservados</span>
                <span className="tabular-nums font-medium">{formatEUR(forward.revenue)}</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-stone-500 dark:text-stone-400">
              Datos verificables sobre reservas ya confirmadas. No es un forecast.
            </p>
          </BlockCard>

          <BlockCard title="Extractos mensuales" icon={DownloadSimple} testId="pms-inv-statements">
            <ul className="divide-y divide-stone-100 dark:divide-stone-900 -mt-1">
              {/* Mes en curso */}
              <li className="py-2 flex items-center gap-3">
                <div className="text-xs text-stone-500 tabular-nums w-16 shrink-0">2028-08</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-900 dark:text-stone-100">Agosto · En curso</div>
                  <div className="text-[11px] text-stone-500 dark:text-stone-400">
                    Neto estimado: {formatEUR(breakdown.owner_net_estimated)}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  Abierto
                </span>
              </li>
              {statements.map((s) => (
                <StatementRow key={s.statement_id} s={s} onOpen={() => setStatementModal(s)} />
              ))}
            </ul>
          </BlockCard>
        </div>
      </div>

      {/* Financial breakdown */}
      <div className="mt-5">
        <button
          type="button"
          onClick={() => setShowBreakdown((v) => !v)}
          className="text-sm inline-flex items-center gap-1.5 text-stone-900 dark:text-stone-100 hover:opacity-80"
          data-testid="pms-inv-breakdown-toggle"
        >
          <CaretDown size={14} className={`transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
          {showBreakdown ? "Ocultar desglose financiero" : "Ver desglose financiero"}
        </button>
        {showBreakdown && (
          <div className="mt-2 dv-card" data-testid="pms-inv-breakdown">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-3">
              {DEMO_MONTH_LABEL} · Estimado (mes en curso)
            </div>
            <FinancialTable b={breakdown} />
            <p className="mt-3 text-[11px] text-stone-500 dark:text-stone-400">
              El neto final se confirma en el extracto mensual cerrado. Los gastos operativos
              varían cada mes.
            </p>
          </div>
        )}
      </div>

      {statementModal && (
        <StatementModal statement={statementModal} villa={villa} onClose={() => setStatementModal(null)} />
      )}
      {smartModalOpen && (
        <SmartAllocationModal pool={pool} onClose={() => setSmartModalOpen(false)} />
      )}
      {ownerModalOpen && (
        <OwnerRequestModal villa={villa} onClose={() => setOwnerModalOpen(false)} />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function VillaHeader({ villa, pool }) {
  return (
    <div className="mb-4 dv-card border-l-4 border-l-amber-400 flex items-start gap-3" data-testid="pms-inv-header">
      <Info size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" weight="fill" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {villa.name}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">
            {pool?.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            Vista previa · Datos de demostración
          </span>
        </div>
        <div className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          Esta vista representa cómo podrás seguir la ocupación y rendimiento de tu villa cuando
          Dolce Villas entre en fase operativa.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI card with optional hint
// ---------------------------------------------------------------------------

function Kpi({ label, value, sub, tone = "stone", hint }) {
  const toneClass = tone === "terracotta"
    ? "text-[#9C5236] dark:text-amber-300"
    : tone === "emerald"
    ? "text-emerald-700 dark:text-emerald-300"
    : "text-stone-900 dark:text-stone-100";
  return (
    <div className="dv-card" title={hint || ""}>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 flex items-center gap-1">
        {label}
        {hint && <Info size={11} className="text-stone-400 dark:text-stone-500" />}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-stone-500 dark:text-stone-400">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month status bar
// ---------------------------------------------------------------------------

function MonthStatusBar({ statements }) {
  const lastClosed = statements.find((s) => s.status === "closed");
  return (
    <div className="mb-5 flex flex-wrap gap-2 items-center text-xs" data-testid="pms-inv-month-status">
      <span className="inline-flex items-center gap-1 text-stone-700 dark:text-stone-200">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        <strong>{DEMO_MONTH_LABEL}</strong> · En curso
      </span>
      {lastClosed && (
        <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {monthLabel(lastClosed.month)} · Cerrado ✓
        </span>
      )}
    </div>
  );
}

function monthLabel(prefix) {
  const [, m] = prefix.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return names[Number(m) - 1] || prefix;
}

// ---------------------------------------------------------------------------
// Villa calendar (single villa, month)
// ---------------------------------------------------------------------------

function VillaCalendar({ villa }) {
  const { firstISO, lastISO } = useMemo(() => monthRange(DEMO_MONTH), []);
  const days = useMemo(
    () => datesBetweenExclusive(firstISO, addDays(lastISO, 1)),
    [firstISO, lastISO],
  );
  const reservations = reservationsForVilla(villa.villa_id);
  const blocks = blocksForVilla(villa.villa_id);

  // Detect hover-capable device once. Desktop → hover-based popover; touch →
  // tap toggles; tap outside closes. Same content component either way.
  const [hoverCapable] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(hover: hover) and (pointer: fine)").matches,
  );
  const [tip, setTip] = useState(null); // { iso, anchor, kind, res?, block? }

  const dayState = (iso) => {
    const res = reservations.find((r) => isNightInStay(iso, r.check_in, r.check_out));
    if (res) return { kind: "reserved", res };
    const owner = blocks.find((b) => b.type === "OWNER_STAY" && isDayInBlock(iso, b));
    if (owner) return { kind: "owner", block: owner };
    const other = blocks.find((b) => b.type !== "OWNER_STAY" && isDayInBlock(iso, b));
    if (other) return { kind: "blocked", block: other };
    return { kind: "available" };
  };

  const showTip = (iso, st, evt) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    setTip({ iso, ...st, anchor: rect });
  };
  const hideTip = () => setTip(null);
  const toggleTipTap = (iso, st, evt) => {
    // Read rect synchronously: React nullifies event.currentTarget after the
    // handler returns, so a functional updater would crash on `.getBoundingClientRect()`.
    const rect = evt.currentTarget.getBoundingClientRect();
    setTip((cur) => {
      if (cur && cur.iso === iso) return null;
      return { iso, ...st, anchor: rect };
    });
  };

  // Close on outside tap (touch devices only — hover devices already hide on mouseleave)
  useEffect(() => {
    if (hoverCapable || !tip) return undefined;
    const onDocDown = (e) => {
      if (e.target.closest("[data-inv-cal-day]")) return;
      if (e.target.closest("[data-inv-cal-popover]")) return;
      setTip(null);
    };
    document.addEventListener("pointerdown", onDocDown, true);
    return () => document.removeEventListener("pointerdown", onDocDown, true);
  }, [hoverCapable, tip]);

  return (
    <BlockCard title={`Tu villa · ${DEMO_MONTH_LABEL}`} icon={House} testId="pms-inv-calendar">
      <div className="grid grid-cols-7 gap-1 text-center text-[10px]">
        {["L","M","X","J","V","S","D"].map((d) => (
          <div key={d} className="text-stone-400 dark:text-stone-500 pb-1">{d}</div>
        ))}
        {(() => {
          // Align first day to weekday (Mon=0)
          const firstDate = new Date(firstISO + "T00:00:00Z");
          const dowMonFirst = (firstDate.getUTCDay() + 6) % 7;
          return Array.from({ length: dowMonFirst }).map((_, i) => (
            <div key={`spacer-${i}`} />
          ));
        })()}
        {days.map((iso) => {
          const st = dayState(iso);
          const isToday = iso === DEMO_TODAY_ISO;
          const bg = st.kind === "reserved"  ? "bg-[#9C5236] text-white"
                  : st.kind === "owner"     ? "bg-stone-800 dark:bg-stone-300 text-white dark:text-stone-900"
                  : st.kind === "blocked"   ? "bg-red-400 dark:bg-red-500 text-white"
                  : "bg-stone-50 dark:bg-stone-900 text-stone-500 dark:text-stone-400";
          const ring = isToday ? "ring-2 ring-amber-500" : "";
          const interactable = st.kind !== "available";
          const hoverHandlers = hoverCapable && interactable ? {
            onMouseEnter: (e) => showTip(iso, st, e),
            onMouseLeave: hideTip,
            onFocus: (e) => showTip(iso, st, e),
            onBlur: hideTip,
          } : {};
          const tapHandlers = !hoverCapable && interactable ? {
            onClick: (e) => toggleTipTap(iso, st, e),
          } : {};
          return (
            <button
              key={iso}
              type="button"
              disabled={!interactable}
              data-inv-cal-day
              className={`aspect-square rounded ${bg} ${ring} text-[10px] tabular-nums transition-opacity min-h-[44px] sm:min-h-0 ${interactable ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
              data-testid={`pms-inv-cal-${iso}`}
              data-kind={st.kind}
              {...hoverHandlers}
              {...tapHandlers}
            >
              {Number(iso.slice(-2))}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-stone-500 dark:text-stone-400">
        {CAL_LEGEND.map((l) => (
          <span key={l.key} className="inline-flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${l.cls}`} />
            {l.label}
          </span>
        ))}
      </div>

      {tip && (
        <CalendarPopover
          tip={tip}
          hoverCapable={hoverCapable}
          onClose={hideTip}
        />
      )}
    </BlockCard>
  );
}

/**
 * Popover contextual del calendario. `hoverCapable=true` → sin close button
 * (mouseleave del día lo cierra); `hoverCapable=false` → tap outside cierra
 * (gestionado por el parent) o botón X.
 */
const POP_WIDTH = 240;
const POP_HEIGHT_EST = 170;

function CalendarPopover({ tip, hoverCapable, onClose }) {
  const anchor = tip.anchor;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const centerX = anchor.left + anchor.width / 2;
  let left = centerX - POP_WIDTH / 2;
  left = Math.max(8, Math.min(vw - POP_WIDTH - 8, left));
  let top = anchor.bottom + 8;
  if (top + POP_HEIGHT_EST > vh - 8) top = Math.max(8, anchor.top - POP_HEIGHT_EST - 8);
  return (
    <div
      data-inv-cal-popover
      role="dialog"
      style={{
        position: "fixed",
        top,
        left,
        width: POP_WIDTH,
        zIndex: 60,
      }}
      className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-xl p-3 text-xs text-stone-700 dark:text-stone-200"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-400">{tip.iso}</div>
        {!hoverCapable && (
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center -mt-1 -mr-1"
            aria-label="Cerrar"
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>
      {tip.kind === "reserved" && (
        <>
          <div className="font-semibold text-stone-900 dark:text-stone-100 mb-1">
            Reserva confirmada
          </div>
          <div className="space-y-0.5">
            <PopRow label="Fechas"   value={`${tip.res.check_in} → ${tip.res.check_out}`} />
            <PopRow label="Noches"   value={String(tip.res.nights)} />
            <PopRow label="Canal"    value={getChannel(tip.res.channel).label} />
            <PopRow label="Ingresos" value={formatEUR(tip.res.accommodation_revenue_eur)} />
            <PopRow label="Estado"   value={`${BOOKING_STATUS_LABEL[tip.res.booking_status]} · ${STAY_STATUS_LABEL[tip.res.stay_status]}`} />
          </div>
          <div className="mt-2 text-[10px] text-stone-400 dark:text-stone-500">
            Datos del huésped no incluidos en esta vista.
          </div>
        </>
      )}
      {tip.kind === "owner" && (
        <>
          <div className="font-semibold text-stone-900 dark:text-stone-100 mb-1">
            Uso propietario
          </div>
          <div className="space-y-0.5">
            <PopRow label="Fechas" value={`${tip.block.start_date} → ${tip.block.end_date}`} />
            <PopRow label="Noches" value={String(tip.block.nights)} />
            <PopRow label="Estado" value={tip.block.status || "programado"} />
          </div>
        </>
      )}
      {tip.kind === "blocked" && (
        <>
          <div className="font-semibold text-stone-900 dark:text-stone-100 mb-1">
            Villa no disponible
          </div>
          <div className="space-y-0.5">
            <PopRow label="Fechas" value={`${tip.block.start_date} → ${tip.block.end_date}`} />
            <PopRow label="Noches" value={String(tip.block.nights)} />
            <PopRow label="Motivo" value={tip.block.type === "MAINTENANCE" ? "Mantenimiento" : tip.block.type} />
            {tip.block.resolved_at && (
              <PopRow label="Resuelto" value={String(tip.block.resolved_at).slice(0, 10)} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PopRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 leading-relaxed">
      <span className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">{label}</span>
      <span className="text-right text-stone-800 dark:text-stone-100 tabular-nums">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upcoming reservation row
// ---------------------------------------------------------------------------

function UpcomingRow({ r }) {
  return (
    <li className="py-2 flex items-center gap-3" data-testid={`pms-inv-upcoming-${r.reservation_id}`}>
      <div className="w-16 shrink-0 text-xs text-stone-500 dark:text-stone-400 tabular-nums">
        {r.check_in.slice(5)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-stone-900 dark:text-stone-100">
          {shortRange(r.check_in, r.check_out)} · {getChannel(r.channel).label}
        </div>
        <div className="text-[11px] text-stone-500 dark:text-stone-400">
          {r.nights} noches · {BOOKING_STATUS_LABEL[r.booking_status]}
        </div>
      </div>
      <div className="text-sm tabular-nums text-stone-900 dark:text-stone-100 shrink-0">
        {formatEUR(r.accommodation_revenue_eur)}
      </div>
    </li>
  );
}

function shortRange(a, b) {
  return `${a.slice(5)} → ${b.slice(5)}`;
}

// ---------------------------------------------------------------------------
// Evolution chart (SVG bars, single metric)
// ---------------------------------------------------------------------------

function EvolutionChart({ series, metric }) {
  const values = series.map((row) => {
    if (metric === "revenue") return row.revenue;
    if (metric === "occupancy") return row.occupancy;
    // `null` cuando el mes no tiene desglose de costes. Se propaga tal cual:
    // convertirlo en 0 aquí dibujaría una barra vacía, que el ojo lee como
    // "ese mes el propietario no ganó nada" en vez de "no lo sabemos".
    return row.owner_net_estimated;
  });
  const known = values.filter((v) => v != null);
  const max = Math.max(...known, 1);
  const barColor = metric === "occupancy" ? "#10b981" : metric === "owner_net" ? "#9C5236" : "#1f2937";
  const formatValue = metric === "occupancy"
    ? (v) => `${Math.round(v * 100)}%`
    : (v) => formatEUR(v);

  return (
    <div className="mt-2">
      <div className="flex items-end gap-2 h-32">
        {series.map((row, i) => {
          const missing = values[i] == null;
          const h = missing ? 0 : (values[i] / max) * 100;
          const isCurrent = row.month === DEMO_MONTH;
          return (
            <div key={row.month} className="flex-1 flex flex-col items-center justify-end gap-1">
              <div className={missing
                ? "text-[9px] text-stone-400 dark:text-stone-500"
                : "text-[9px] text-stone-500 dark:text-stone-400 tabular-nums"}>
                {missing ? "—" : formatValue(values[i])}
              </div>
              {missing ? (
                // Hueco dibujado como hueco: contorno punteado y sin relleno.
                <div
                  className="w-full rounded-t border border-dashed border-stone-300 dark:border-stone-600"
                  style={{ height: "18%" }}
                  data-testid={`pms-inv-evolution-missing-${row.month}`}
                  title={`${row.label} · sin desglose de costes `
                    + `(${row.owner_net_missing_reason}): el neto aprobado `
                    + "necesita tasa, canal, procesamiento y gastos operativos."}
                />
              ) : (
              <div
                className="w-full rounded-t"
                style={{
                  height: `${Math.max(3, h)}%`,
                  background: isCurrent ? barColor : `${barColor}80`,
                }}
                title={`${row.label} · ${formatValue(values[i])}`}
              />
              )}
              <div className={`text-[10px] ${isCurrent ? "font-semibold text-stone-900 dark:text-stone-100" : "text-stone-400"}`}>
                {row.label}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[11px] text-stone-500 dark:text-stone-400">
        {metric === "owner_net"
          ? "El neto del propietario solo se calcula donde hay desglose de costes "
            + "(tasa, canal, procesamiento y gastos operativos). Enero–julio no lo "
            + "tienen y se muestran como dato no disponible; agosto usa el reparto real."
          : "Enero–julio se derivan del acumulado anual demo; agosto refleja las "
            + "cifras reales del dataset."}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// YTD block
// ---------------------------------------------------------------------------

function YtdBlock({ ytd, ownerNet }) {
  const [showRevPar, setShowRevPar] = useState(false);
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <YtdRow label="Ingresos acumulados" value={formatEUR(ytd.revenue)} />
        <YtdRow label="Ocupación media"     value={formatPct(ytd.occupancy)} />
        <YtdRow label="ADR medio"           value={formatEUR(ytd.adr)} />
        {/* El acumulado del año solo existe si TODOS sus meses tienen
            desglose de costes, y hoy solo lo tiene uno. Sumar los modelados y
            llamar al resultado "Neto propietario" sería enseñar una cifra que
            parece completa y se queda corta. Antes aquí había un recorte
            del 45% a ojo sobre el bruto, justo encima de la tabla que deduce
            un 15%. */}
        <YtdRow
          label="Neto propietario"
          value={ownerNet.complete ? formatEUR(ownerNet.value) : "No modelado"}
          muted={!ownerNet.complete}
          title={ownerNet.complete ? undefined
            : `Sin desglose de costes en ${ownerNet.unmodelled_months} de `
              + `${ownerNet.unmodelled_months + ownerNet.modelled_months} meses `
              + `(${ownerNet.reason}). El neto aprobado necesita tasa, canal, `
              + "procesamiento y gastos operativos."}
        />
      </div>
      <button
        type="button"
        onClick={() => setShowRevPar((v) => !v)}
        className="mt-3 text-[11px] text-stone-500 dark:text-stone-400 hover:underline inline-flex items-center gap-1"
      >
        {showRevPar ? "Ocultar" : "Ver más"} <CaretDown size={10} className={showRevPar ? "rotate-180" : ""} />
      </button>
      {showRevPar && (
        <div className="mt-2 text-xs text-stone-700 dark:text-stone-300">
          RevPAR: <span className="font-medium tabular-nums">{formatEUR(ytd.revpar)}</span>
          <div className="text-[11px] text-stone-500 dark:text-stone-400 mt-1">
            Revenue Per Available Room · ingresos por villa-noche disponible.
          </div>
        </div>
      )}
    </>
  );
}

function YtdRow({ label, value, muted = false, title }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">{label}</div>
      <div
        className={muted
          ? "text-sm text-stone-400 dark:text-stone-500 italic"
          : "text-sm text-stone-900 dark:text-stone-100 tabular-nums"}
        title={title}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Channel row (right sidebar)
// ---------------------------------------------------------------------------

function ChannelRow({ row }) {
  const pct = Math.round(row.share * 100);
  return (
    <div>
      <div className="flex justify-between text-xs text-stone-700 dark:text-stone-300">
        <span>{row.channel.label}</span>
        <span className="tabular-nums text-stone-500 dark:text-stone-400">
          {formatEUR(row.revenue)} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-stone-100 dark:bg-stone-800 overflow-hidden">
        <div
          className="h-full bg-stone-800 dark:bg-stone-200"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner usage block + request modal
// ---------------------------------------------------------------------------

function OwnerUsageBlock({ usage, upcoming, onRequest }) {
  const pct = Math.min(100, (usage.used / 60) * 100);
  const recent = (usage.requests || [])
    .slice()
    .sort((a, b) => (b.requested_at || "") < (a.requested_at || "") ? -1 : 1)
    .slice(0, 3);
  return (
    <>
      <div className="flex justify-between text-sm mb-1 text-stone-700 dark:text-stone-300">
        <span>Uso anual</span>
        <span className="tabular-nums font-medium">{usage.used} / 60 noches</span>
      </div>
      <div className="h-2 rounded-full bg-stone-100 dark:bg-stone-800 overflow-hidden">
        <div className="h-full bg-stone-800 dark:bg-stone-200" style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      {upcoming && (
        <div className="mt-3 text-xs text-stone-700 dark:text-stone-300">
          Próxima estancia aprobada: <strong>{upcoming.start_date} → {upcoming.end_date}</strong> · {upcoming.nights} noches.
        </div>
      )}
      {recent.length > 0 && (
        <ul className="mt-3 space-y-1 text-[11px]" data-testid="pms-inv-owner-recent">
          {recent.map((r) => (
            <li key={r.request_id} className="flex items-center gap-2 text-stone-600 dark:text-stone-400">
              <OwnerStatusBadge status={r.status} />
              <span className="tabular-nums">{r.start_date} → {r.end_date}</span>
              <span className="text-stone-400">· {r.nights}n</span>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={onRequest}
        className="mt-3 text-sm px-3 py-1.5 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 inline-flex items-center gap-1.5 hover:opacity-90"
        data-testid="pms-inv-owner-request"
      >
        <Bed size={14} /> Solicitar fechas
      </button>
    </>
  );
}

function OwnerStatusBadge({ status }) {
  const map = {
    requested: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200" },
    approved:  { label: "Aprobada",  cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200" },
    completed: { label: "Completada", cls: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-200" },
    rejected:  { label: "Rechazada", cls: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200" },
    cancelled: { label: "Cancelada", cls: "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400" },
  };
  const it = map[status] || map.requested;
  return (
    <span className={`inline-block text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${it.cls}`}>
      {it.label}
    </span>
  );
}

function OwnerRequestModal({ villa, onClose }) {
  const [start, setStart] = useState(addDays(DEMO_TODAY_ISO, 40));
  const [end, setEnd] = useState(addDays(DEMO_TODAY_ISO, 47));
  const [submitted, setSubmitted] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // request_id | null

  const nights = Math.max(0, Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 86400000,
  ));

  const request = {
    villa_id: villa.villa_id,
    start_date: start,
    end_date: end,
    status: "requested",
    requested_at: new Date().toISOString(),
  };
  const val = ownerStayValidation(request);

  const handleSend = () => {
    if (!val.all_ok) return;
    const created = createOwnerStayRequest({
      villa_id: villa.villa_id,
      investor_user_id: DEMO_INVESTOR.user_id,
      investor_name: DEMO_INVESTOR.name,
      start_date: start,
      end_date: end,
    });
    setConfirmed(created.request_id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-stone-950 w-full sm:max-w-[440px] sm:rounded-lg rounded-t-2xl shadow-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Solicitar uso propietario</div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100 mt-0.5">{villa.name}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center">
            <X size={14} weight="bold" />
          </button>
        </div>

        {!confirmed ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Check-in</div>
                <input type="date" value={start} onChange={(e) => { setStart(e.target.value); setSubmitted(false); }}
                  className="w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Check-out</div>
                <input type="date" value={end} min={start} onChange={(e) => { setEnd(e.target.value); setSubmitted(false); }}
                  className="w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200" />
              </div>
            </div>

            <div className="mt-3 text-xs text-stone-500 dark:text-stone-400">
              Noches: <span className="tabular-nums text-stone-900 dark:text-stone-100 font-medium">{nights}</span>
            </div>

            {submitted && (
              <div className="mt-3 space-y-1">
                <Check ok={val.availability_ok} label="Villa disponible" detail={val.conflicts.slice(0, 2).join(" · ")} />
                <Check ok={val.notice_ok}       label="Antelación ≥ 30 días" detail={val.notice_days != null ? `${val.notice_days} días` : ""} />
                <Check ok={val.quota_ok}        label="Dentro de cupo (60/año)" detail={`Tras aprobar: ${val.quota_projected}/60`} />
                <Check ok={val.blackout_ok}     label="Fuera de blackout" detail={val.blackout ? val.blackout.label : ""} />
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <button onClick={onClose} className="text-sm px-3 py-2 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200">
                Cancelar
              </button>
              {!submitted && (
                <button onClick={() => setSubmitted(true)}
                  className="text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90">
                  Validar fechas
                </button>
              )}
              {submitted && val.all_ok && (
                <button onClick={handleSend}
                  className="text-sm px-3 py-2 rounded bg-emerald-600 dark:bg-emerald-500 text-white hover:opacity-90 inline-flex items-center gap-1.5"
                  data-testid="pms-inv-owner-send">
                  <Sparkle size={12} /> Enviar solicitud
                </button>
              )}
            </div>
            <p className="mt-3 text-[10px] text-stone-400">
              Demo · persiste en memoria de sesión. En producción entrará en el flujo Owner Stays admin.
            </p>
          </>
        ) : (
          <div className="text-center py-6">
            <div className="text-2xl mb-2">✅</div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">
              Solicitud enviada
            </div>
            <div className="mt-1 text-xs text-stone-500 dark:text-stone-400 font-mono">
              {confirmed}
            </div>
            <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">
              El equipo la revisará en Owner Stays. Verás el estado (approved/rejected)
              aquí sin refrescar cuando se decida.
            </p>
            <button onClick={onClose} className="mt-4 text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900">
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Check({ ok, label, detail }) {
  return (
    <div className={`text-xs flex items-center gap-2 ${ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>
      {ok ? "✓" : "✕"}
      <span>{label}</span>
      {detail && <span className="text-[11px] text-stone-500 dark:text-stone-400">· {detail}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Smart allocation modal
// ---------------------------------------------------------------------------

function SmartAllocationModal({ pool, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-stone-950 w-full sm:max-w-[480px] sm:rounded-lg rounded-t-2xl shadow-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Cómo funciona</div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">Distribución inteligente</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="text-sm text-stone-700 dark:text-stone-300 space-y-2">
          <p>
            Tu villa forma parte del pool <strong>{pool?.label}</strong> ({pool?.villa_count} villas
            equivalentes). Cuando llega una nueva reserva, el motor:
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Excluye villas sin disponibilidad (reservas, owner stays, mantenimientos).</li>
            <li>Mide el equilibrio de cada villa restante combinando ingresos y ocupación del mes en curso y del acumulado anual.</li>
            <li>Asigna la reserva a la villa con menos "peso" acumulado, favoreciendo un reparto equilibrado.</li>
            <li>Resuelve empates con reglas deterministas: menos noches futuras reservadas, más tiempo desde la última asignación, y por último el identificador de la villa. Nunca aleatorio.</li>
          </ol>
          <p className="text-[11px] text-stone-500 dark:text-stone-400">
            El sistema busca equilibrar oportunidades comerciales. No garantiza ingresos u
            ocupación idénticos entre villas.
          </p>
        </div>
        <div className="mt-4 text-right">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Statement row + modal
// ---------------------------------------------------------------------------

function StatementRow({ s, onOpen }) {
  return (
    <li className="py-2 flex items-center gap-3">
      <div className="text-xs text-stone-500 tabular-nums w-16 shrink-0">{s.month}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-stone-900 dark:text-stone-100">
          {monthLabel(s.month)} · Cerrado ✓
        </div>
        <div className="text-[11px] text-stone-500 dark:text-stone-400">
          Neto: {formatEUR(s.owner_net_final_eur)}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="text-xs px-2.5 py-1 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-500"
      >
        Ver extracto
      </button>
    </li>
  );
}

function StatementModal({ statement, villa, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-stone-950 w-full sm:max-w-[480px] sm:rounded-lg rounded-t-2xl shadow-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">
              {villa.name} · {monthLabel(statement.month)}
            </div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">Extracto mensual</div>
            <span className="mt-1 inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              Cerrado ✓
            </span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center">
            <X size={14} weight="bold" />
          </button>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <StmtRow label="Accommodation revenue" value={statement.accommodation_revenue_eur} />
            <StmtRow label="Tax (10 %)"            value={-statement.tax_eur} />
            <StmtRow label="Canal / OTA"           value={-statement.channel_cost_eur} />
            <StmtRow label="Operating expenses"    value={-statement.operating_expenses_final_eur} />
            <StmtRow label="Management (15 %)"     value={-statement.management_eur} />
            <tr className="border-t border-stone-200 dark:border-stone-800">
              <td className="pt-2 font-semibold text-stone-900 dark:text-stone-100">Owner net (final)</td>
              <td className="pt-2 text-right font-semibold text-stone-900 dark:text-stone-100 tabular-nums">
                {formatEUR(statement.owner_net_final_eur)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button disabled title="Disponible cuando esté conectado el generador de PDF"
            className="text-sm px-3 py-2 rounded border border-stone-300 dark:border-stone-700 text-stone-400 dark:text-stone-500 cursor-not-allowed inline-flex items-center gap-1.5">
            <DownloadSimple size={12} /> Descargar PDF
          </button>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function StmtRow({ label, value }) {
  return (
    <tr>
      <td className="py-1 text-stone-600 dark:text-stone-400">{label}</td>
      <td className="py-1 text-right tabular-nums text-stone-900 dark:text-stone-100">
        {formatEUR(value)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Financial breakdown table
// ---------------------------------------------------------------------------

function FinancialTable({ b }) {
  const opexLabel = b.opex_kind === "estimated"
    ? "− Operating expenses (estimated)"
    : "− Operating expenses";
  return (
    <table className="w-full text-sm">
      <tbody>
        <BdRow label="Accommodation revenue" value={b.accommodation_revenue} />
        <BdRow label="− Taxes (10 %)"        value={-b.tax} />
        <BdRow label="− OTA commissions / direct fee" value={-b.channel_cost} />
        <BdRow
          label="− Payment processing (DOKU)"
          value={-b.payment_processing}
          hint="Estimación demo basada en 2,8 % + Rp2.000 por transacción con tarjeta. Solo aplica a reservas directas cobradas con tarjeta. La pasarela definitiva sigue por definir."
        />
        <BdRow
          label={opexLabel}
          value={-b.opex}
          hint="Importe mensual variable — no es un porcentaje del revenue. Se conoce definitivamente al cierre del mes."
        />
        <tr className="border-t border-stone-200 dark:border-stone-800">
          <td className="pt-2 font-medium text-stone-900 dark:text-stone-100">Management base</td>
          <td className="pt-2 text-right font-medium text-stone-900 dark:text-stone-100 tabular-nums">
            {formatEUR(b.management_base)}
          </td>
        </tr>
        <BdRow label="− Management fee (15 %)" value={-b.management} />
        <tr className="border-t border-stone-200 dark:border-stone-800">
          <td className="pt-2 font-semibold text-stone-900 dark:text-stone-100">Owner net (estimated)</td>
          <td className="pt-2 text-right font-semibold text-stone-900 dark:text-stone-100 tabular-nums">
            {formatEUR(b.owner_net_estimated)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function BdRow({ label, value, hint }) {
  return (
    <tr>
      <td className="py-1 text-stone-600 dark:text-stone-400" title={hint || ""}>
        <span className="inline-flex items-center gap-1">
          {label}
          {hint && <Info size={11} className="text-stone-400 dark:text-stone-500" />}
        </span>
      </td>
      <td className="py-1 text-right tabular-nums text-stone-900 dark:text-stone-100">
        {formatEUR(value)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Generic BlockCard wrapper
// ---------------------------------------------------------------------------

function BlockCard({ title, icon: Icon, testId, headerRight, children }) {
  return (
    <div className="dv-card" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
          {Icon && <Icon size={12} weight="bold" />}
          {title}
        </div>
        {headerRight && <div className="ml-auto">{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}
